import { LitElement, css, html, unsafeCSS } from "lit";
import { unsafeHTML } from "lit/directives/unsafe-html.js";

export class RdDropDownPanel extends LitElement {
  sampleItems = [
    //real sample items with bx icons
    { leftIcon: "", value: "Apple", rightIcon: ``, groupName: "", isSelected: false },
    { leftIcon: ``, value: "Banana", rightIcon: "", groupName: "" },
    { leftIcon: ``, value: "Mango", rightIcon: ``, groupName: "" },
    { leftIcon: ``, value: "Broccoli", rightIcon: "", groupName: "" },
    { leftIcon: ``, value: "Carrot", rightIcon: "", groupName: "" },
    { leftIcon: ``, value: "Bread", rightIcon: "", groupName: "" },
  ];
  static properties = {
    open: { type: Boolean, reflect: true },
    //{leftIcon, value, rightIcon, groupName, isSelected, disabled, children}
    items: { type: Array },
    width: { type: String },
    height: { type: String },
    //{top, top-left, left, bottom-left, bottom, bottom-right, right, top-right}
    placement: { type: String, reflect: true },
    closeOnSelect: { type: Boolean, reflect: true },
    multiSelection: { type: Boolean, reflect: true },
    target: { type: Object },
    targetSelector: { type: String },

    offsetX: { type: Number, reflect: true },
    offsetY: { type: Number, reflect: true },
    searchable: { type: Boolean, reflect: true },
    _searchValue: { type: String, state: true },
    _isFiltering: { type: Boolean, state: true },
    _displayItems: { type: Array, state: true }, // Items shown in UI (filtered)
    _lockedWidth: { type: String, state: true }, // Locked width during search
    size: { type: String },
    maxVisibleItems: { type: Number },
    onOpen: { type: Function },
    onClose: { type: Function },
    onSelect: { type: Function },
    onHover: { type: Function },
    isHtml: { type: Boolean, reflect: true },
  };
  // createRenderRoot() {
  //   return this;
  // }
  constructor() {
    super();
    this.open = false;
    this.closeOnSelect = false;
    this._multiSelection = false;
    this.size = "auto";
    this._items = []; // Internal store for original items (reference preserved)
    this._displayItems = []; // Items to display (filtered)
    this.width = "200px";
    this.height = "auto";
    this.placement = "bottom-left";
    this.target = null;
    this.offsetX = 0;
    this.offsetY = 0;
    this._searchValue = "";
    this._isFiltering = false;
    this._lockedWidth = null;
    this.searchable = false;
    this.maxVisibleItems = 10;
    this.isHtml = false;
    this.targetSelector = "";
    this._selectedIndices = new Set(); // Track selected indices
  }

  createRenderRoot() {
    return this;
  }

  /**
   * Getter for items - returns original items (not filtered)
   */
  get items() {
    return this._items;
  }

  /**
   * Setter for items - stores reference (no copy) and updates display
   */
  set items(value) {
    this._items = value || [];
    // CRITICAL: Clear selection state when new items are set (singleton pattern)
    // This prevents previous dropdown's selections from affecting the next dropdown
    this._selectedIndices.clear();
    this._searchValue = "";

    // Initialize selection from items' selected property if provided
    this._items.forEach((item, index) => {
      if (item.selected === true) {
        this._selectedIndices.add(index);
      }
    });

    this._updateDisplayItems();
    this.requestUpdate();
  }

  /**
   * Get currently selected item (single selection)
   */
  get selectedItem() {
    if (this._selectedIndices.size === 0) return null;
    const index = Array.from(this._selectedIndices)[0];
    return this._items[index] || null;
  }

  /**
   * Set selected item (single selection)
   */
  set selectedItem(item) {
    this._selectedIndices.clear();
    if (item) {
      const index = this._items.indexOf(item);
      if (index !== -1) {
        this._selectedIndices.add(index);
      }
    }
    this._updateDisplayItems();
    this.requestUpdate();
  }

  /**
   * Get all selected items (multi-selection)
   */
  get selectedItems() {
    return Array.from(this._selectedIndices)
      .map((index) => this._items[index])
      .filter((item) => item !== undefined);
  }

  /**
   * Set selected items (multi-selection)
   */
  set selectedItems(items) {
    this._selectedIndices.clear();
    if (Array.isArray(items)) {
      items.forEach((item) => {
        const index = this._items.indexOf(item);
        if (index !== -1) {
          this._selectedIndices.add(index);
        }
      });
    }
    this._updateDisplayItems();
    this.requestUpdate();
  }

  /**
   * Get selected index (single selection)
   */
  get selectedIndex() {
    if (this._selectedIndices.size === 0) return -1;
    return Array.from(this._selectedIndices)[0];
  }

  /**
   * Set selected index (single selection)
   */
  set selectedIndex(index) {
    this._selectedIndices.clear();
    if (index >= 0 && index < this._items.length) {
      this._selectedIndices.add(index);
    }
    this._updateDisplayItems();
    this.requestUpdate();
  }

  /**
   * Get all selected indices (multi-selection)
   */
  get selectedIndices() {
    return Array.from(this._selectedIndices);
  }

  /**
   * Set selected indices (multi-selection)
   */
  set selectedIndices(indices) {
    this._selectedIndices.clear();
    if (Array.isArray(indices)) {
      indices.forEach((index) => {
        if (index >= 0 && index < this._items.length) {
          this._selectedIndices.add(index);
        }
      });
    }
    this._updateDisplayItems();
    this.requestUpdate();
  }

  /**
   * Setter for multiSelection - auto-adjust closeOnSelect
   */
  // set multiSelection(value) {
  //   this._multiSelection = value;
  //   // When enabling multi-selection, automatically disable closeOnSelect
  //   if (value === true) {
  //     this.closeOnSelect = false;
  //   }
  //   this.requestUpdate();
  // }

  // /**
  //  * Getter for multiSelection
  //  */
  // get multiSelection() {
  //   return this._multiSelection;
  // }

  /**
   * Update display items based on search filter
   */
  _updateDisplayItems() {
    const query = (this._searchValue || "").toLowerCase().trim();

    if (!query) {
      // No filter - show all items with selection state and original index
      this._displayItems = this._items.map((item, index) => ({
        ...item,
        selected: this._selectedIndices.has(index),
        _originalIndex: index, // Always keep track of original index
      }));
    } else {
      // Filter items based on search
      this._displayItems = this._items
        .map((item, index) => ({
          ...item,
          selected: this._selectedIndices.has(index),
          _originalIndex: index, // Keep track of original index
        }))
        .filter((item) => {
          const val = String(item.label || item.value || "");
          return val.toLowerCase().includes(query);
        });
    }
  }

  connectedCallback() {
    super.connectedCallback();
    this.style.display = "block";
    window.addEventListener("resize", this._updatePlacement.bind(this));
    window.addEventListener("scroll", this._updatePlacement.bind(this), true);
    window.addEventListener("click", (e) => {
      this._clickHappened(e);
    });
    window.addEventListener(canvasEventsLabels.canvasMouseDown, (e) => {
      console.log("canvas click detected in dropdown panel");
      this._clickHappened(e);
    });

    //..
    this._updatePlacement();
  }
  _clickHappened(e) {
    if (Date.now() - this._openTimestamp < 100) {
      return;
    }

    if (this.targetSelector && e.target.matches(this.targetSelector)) {
      if (!this.open) {
        this.target = e.target;
        this.open = true;
      } else {
        this.open = false;
      }
    } else {
      this.open = false;
    }
  }
  _targetClicked() {}

  updated(changedProperties) {
    if (changedProperties.has("open")) {
      if (this.open) {
        this._openTimestamp = Date.now();
        this.dispatchEvent(new CustomEvent("panel-opened"));
        if (this.onOpen && typeof this.onOpen === "function") {
          this.onOpen();
        }

        // Wait for next frame to ensure panel is rendered and has dimensions
        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            this._updatePlacement();
          });
        });
      } else {
        // Clear search when closing (optional)
        this._searchValue = "";
        this._updateDisplayItems();
        this.dispatchEvent(new CustomEvent("panel-closed"));
        if (this.onClose && typeof this.onClose === "function") {
          this.onClose();
        }
      }
    }

    if (changedProperties.has("target") || changedProperties.has("width") || changedProperties.has("height") || changedProperties.has("placement") || changedProperties.has("offsetX") || changedProperties.has("offsetY")) {
      if (this.open) {
        requestAnimationFrame(() => {
          this._updatePlacement();
        });
      }
    }
  }

  _itemClicked(item, displayIndex) {
    if (Date.now() - this._openTimestamp < 100) {
      return;
    }
    if (item.disabled) return;

    // Get the original index - it's stored in _originalIndex when item is filtered/spread
    const originalIndex = item._originalIndex !== undefined ? item._originalIndex : displayIndex;

    if (originalIndex === -1 || originalIndex >= this._items.length) return; // Invalid index

    if (this.multiSelection) {
      // Multi-selection: toggle selection
      if (this._selectedIndices.has(originalIndex)) {
        this._selectedIndices.delete(originalIndex);
      } else {
        this._selectedIndices.add(originalIndex);
      }
    } else {
      // Single selection: replace selection
      this._selectedIndices.clear();
      this._selectedIndices.add(originalIndex);
    }

    // Update display to reflect selection changes
    this._updateDisplayItems();

    // Get the actual original item (preserving reference)
    const selectedItem = this._items[originalIndex];

    this.dispatchEvent(
      new CustomEvent("item-selected", {
        detail: {
          item: selectedItem,
          index: originalIndex,
          selected: this._selectedIndices.has(originalIndex),
        },
      })
    );

    if (this.onSelect && typeof this.onSelect === "function") {
      this.closeOnSelect && (this.open = false);
      this.onSelect(selectedItem, originalIndex);
    }

    this.requestUpdate();
  }
  _itemHovered(item) {
    if (Date.now() - this._openTimestamp < 100) {
      return;
    }
    if (item.disabled) return;
    this.dispatchEvent(new CustomEvent("item-hovered", { detail: item }));
    if (this.onHover && typeof this.onHover === "function") {
      this.onHover(item);
    }
  }

  _updatePlacement() {
    if (!this.target) {
      let top = this.offsetY;
      let left = this.offsetX;
      this.style.top = `${top}px`;
      this.style.left = `${left}px`;
      return;
    }

    const targetRect = this.target.getBoundingClientRect();
    const panel = this.querySelector(".dropdown-panel");

    // If panel doesn't exist, retry
    if (!panel) {
      console.warn("Panel not found, retrying...");
      requestAnimationFrame(() => this._updatePlacement());
      return;
    }

    const panelRect = panel.getBoundingClientRect();

    // If panel has no dimensions, it's not fully rendered yet
    if (panelRect.width === 0 || panelRect.height === 0) {
      requestAnimationFrame(() => this._updatePlacement());
      return;
    }

    if (this.size && this.size.trim().toLowerCase() === "auto") {
      panel.style.width = `${targetRect.width}px`;
    } else if (this.size && this.size.trim().toLowerCase() === "fit") {
      panel.style.width = `fit-content`;
    }

    let top = 0;
    let left = 0;
    let placement = this.placement || "bottom-left";

    // Smart auto-adjust placement based on viewport with fine-grained control
    const hasRoomAbove = targetRect.top - panelRect.height >= 0;
    const hasRoomBelow = targetRect.bottom + panelRect.height <= window.innerHeight;
    const hasRoomLeft = targetRect.left - panelRect.width >= 0;
    const hasRoomRight = targetRect.right + panelRect.width <= window.innerWidth;
    const hasRoomOnTargetLeft = targetRect.left + panelRect.width <= window.innerWidth;
    const hasRoomOnTargetRight = targetRect.right - panelRect.width >= 0;

    // Adjust vertical position
    if (placement.startsWith("top") && !hasRoomAbove && hasRoomBelow) {
      // Switch from top to bottom
      placement = placement.replace("top", "bottom");
    } else if (placement.startsWith("bottom") && !hasRoomBelow && hasRoomAbove) {
      // Switch from bottom to top
      placement = placement.replace("bottom", "top");
    }

    // Adjust horizontal position for top/bottom placements
    if (placement.startsWith("top") || placement.startsWith("bottom")) {
      if (placement.endsWith("left") && !hasRoomOnTargetLeft && hasRoomOnTargetRight) {
        // Switch from left-aligned to right-aligned
        placement = placement.replace("left", "right");
      } else if (placement.endsWith("right") && !hasRoomOnTargetRight && hasRoomOnTargetLeft) {
        // Switch from right-aligned to left-aligned
        placement = placement.replace("right", "left");
      }
      // Handle center positioning (no -left or -right suffix)
      else if (!placement.includes("-")) {
        if (!hasRoomOnTargetLeft && hasRoomOnTargetRight) {
          placement = placement + "-right";
        } else if (!hasRoomOnTargetRight && hasRoomOnTargetLeft) {
          placement = placement + "-left";
        }
      }
    }

    // Adjust for left/right placements
    if (placement === "left" && !hasRoomLeft && hasRoomRight) {
      placement = "right";
    } else if (placement === "right" && !hasRoomRight && hasRoomLeft) {
      placement = "left";
    }

    // Ensure offsets are numbers
    const offsetX = Number(this.offsetX) || 0;
    const offsetY = Number(this.offsetY) || 0;

    // Calculate position based on adjusted placement - each placement calculated separately
    if (placement === "top") {
      // Top center - align dropdown's left edge with target's left edge
      top = targetRect.top - panelRect.height - offsetY - 1;
      left = targetRect.left + offsetX;
    } else if (placement === "top-left") {
      // Top left - align dropdown's left edge with target's left edge
      top = targetRect.top - panelRect.height - offsetY - 1;
      left = targetRect.left - panelRect.width + offsetX;
    } else if (placement === "top-right") {
      // Top right - align dropdown's right edge with target's right edge
      top = targetRect.top - panelRect.height - offsetY - 1;
      left = targetRect.right - offsetX;
    } else if (placement === "bottom") {
      // Bottom center - align dropdown's left edge with target's left edge
      top = targetRect.bottom + offsetY;
      left = targetRect.left + offsetX;
    } else if (placement === "bottom-left") {
      // Bottom left - align dropdown's left edge with target's left edge
      top = targetRect.bottom + offsetY;
      left = targetRect.left - panelRect.width + offsetX;
    } else if (placement === "bottom-right") {
      // Bottom right - align dropdown's right edge with target's right edge
      top = targetRect.bottom + offsetY;
      left = targetRect.right - offsetX;
    } else if (placement === "left") {
      // Left - align dropdown's right edge with target's left edge
      top = targetRect.top + offsetY;
      left = targetRect.left - panelRect.width - offsetX;
    } else if (placement === "right") {
      // Right - align dropdown's left edge with target's right edge
      top = targetRect.top + offsetY;
      left = targetRect.right + offsetX;
    } else {
      // Default fallback to bottom-left
      top = targetRect.bottom + offsetY;
      left = targetRect.left + offsetX;
    }

    // Final boundary check - keep dropdown within viewport
    if (top < 0) top = offsetY;
    if (left < 0) left = offsetX;
    if (top + panelRect.height > window.innerHeight) {
      top = window.innerHeight - panelRect.height - offsetY;
    }
    if (left + panelRect.width > window.innerWidth) {
      left = window.innerWidth - panelRect.width - offsetX;
    }

    this.style.top = `${top}px`;
    this.style.left = `${left}px`;
  }

  _renderItem(item, displayIndex) {
    if (item.divider) {
      return html` <div class="divider"></div> `;
    }

    return html`
      <div class="dropdown-item ${item.selected ? "selected" : ""} ${item.disabled ? "disabled" : ""}" @click="${() => this._itemClicked(item, displayIndex)}" @mouseover="${() => this._itemHovered(item)}" title="${item.tooltip || ""}">
        ${item.leftIcon ? html`<span class="item-left-icon">${unsafeHTML(String(item.leftIcon || ""))}</span>` : ""}
        <span class="item-value">${unsafeHTML(item.label || "")}</span>
        ${item.rightIcon ? html`<span class="item-right-icon">${unsafeHTML(String(item.rightIcon || ""))}</span>` : ""}
      </div>
    `;
  }

  _renderItems(items) {
    if (!items || items.length === 0) {
      return html` <div class="dropdown-empty">No items available</div> `;
    }

    let groupedItems = {};
    items.forEach((item, index) => {
      if (item.groupName) {
        if (!groupedItems[item.groupName]) {
          groupedItems[item.groupName] = [];
        }
        groupedItems[item.groupName].push({ item, displayIndex: index });
      } else {
        if (!groupedItems[""]) {
          groupedItems[""] = [];
        }
        groupedItems[""].push({ item, displayIndex: index });
      }
    });

    return Object.keys(groupedItems).map((groupName) => {
      return html` <div class="dropdown-group">${groupName ? html` <div class="group-name">${groupName}</div>` : ""} ${groupedItems[groupName].map(({ item, displayIndex }) => this._renderItem(item, displayIndex))}</div> `;
    });
  }

  _onSearchInput(event) {
    const newValue = event.target.value;

    // Lock width when user starts typing (transition from empty to non-empty)
    if (!this._searchValue && newValue && this.size === "fit") {
      const panel = this.querySelector(".dropdown-panel");
      if (panel) {
        this._lockedWidth = `${panel.offsetWidth}px`;
      }
    }

    this._searchValue = newValue;
    this._updateDisplayItems();
    this.requestUpdate();
  }

  _clearSearch(e) {
    this._searchValue = "";
    this._lockedWidth = null; // Unlock width when search is cleared
    this._updateDisplayItems();
    this.requestUpdate();
  }

  _renderContent() {
    if (this.isHtml) {
      if (!this._content) {
        this._content = this.innerHTML;
        this.innerHTML = "";
      }

      return unsafeHTML(this._content);
    }
  }
  _dropdownMainClicked(e) {
    if (!this.isHtml) {
      e.stopPropagation(); // Prevent click from bubbling to window listener
      return;
    }
    if (this.isHtml && !this.closeOnSelect) {
      e.stopPropagation();
    }
  }
  render() {
    // Calculate max-height for items based on maxVisibleItems
    const itemHeight = 30; // Average item height in pixels (including padding)
    const maxHeight = this.maxVisibleItems ? `${this.maxVisibleItems * itemHeight}px` : "none";

    // Determine panel width: use locked width during search, otherwise respect size setting
    let panelWidth = this.width || "200px";
    if (this.size === "fit") {
      panelWidth = this._lockedWidth || "fit-content";
    } else if (this.size === "auto" && this.target) {
      // Auto mode handled in _updatePlacement
      panelWidth = this.width || "200px";
    }

    if (this.isHtml) {
      return html`<div @click="${this._dropdownMainClicked}" class="dropdown-panel" style="${this.open ? "display: flex;" : "display: none;"} width: ${panelWidth}; height: ${this.height || "auto"};">${this._renderContent()}</div>`;
    }
    return html`
      <div @click="${this._dropdownMainClicked}" class="dropdown-panel" style="${this.open ? "display: flex;" : "display: none;"} width: ${panelWidth}; height: ${this.height || "auto"};">
        ${this.searchable
          ? html`
              <div class="search">
                <span class="search-icon"><i class="bx  bx-search"></i> </span>
                <input type="text" class="search-input" placeholder="Search..." @input="${this._onSearchInput}" .value="${this._searchValue || ""}" />
                ${this._searchValue ? html` <button class="clear-btn" @click="${this._clearSearch}" type="button">✕</button> ` : ""}
              </div>
            `
          : ""}
        <div class="dropdown-items" style="max-height: ${maxHeight};">${this._renderItems(this._displayItems)}</div>
      </div>
    `;
  }
}
if (!customElements.get("rd-dropdown-panel")) customElements.define("rd-dropdown-panel", RdDropDownPanel);
